<template>
  <ModalCashRegister v-if="modalShowCashRegister" @closeModal="closeModal" :isOpen="isOpen"
    :label="isOpen ? 'Saldo Akhir' : 'Saldo Awal'" @refreshCashRegister="refreshCashRegister" :relevantCashRegister="relevantCashRegister" />
  <ModalImportMarketplace v-if="modalShowImportMarketplace" @closeModal="closeModalImportMarketplace" />
  <ModalImportTiktok v-if="modalShowImportTiktok" @closeModal="closeModalImportTiktok" />
  <ModalChooseCustomer v-if="modalShowCustomer" @closeModal="closeCustomerModal" @selectCustomer="createOrder" />
  <ModalProductStock v-if="modalShowProductStock" @closeModal="closeModalProductStock" />
  <ConfirmDialog group="headless">
        <template #container="{ message, acceptCallback, rejectCallback }">
            <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
                <div
                    class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
                    <i class="pi pi-question text-5xl"></i>
                </div>
                <span class="font-bold text-2xl block mb-2 mt-6">{{
                    message.header
                }}</span>
                <p class="mb-0">{{ message.message }}</p>
                <div class="flex items-center gap-2 mt-6">
                    <Button label="Save" @click="acceptCallback" class="w-32"></Button>
                    <Button label="Cancel" outlined @click="rejectCallback" class="w-32"></Button>
                </div>
            </div>
        </template>
    </ConfirmDialog>
  <div
    class="bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-dark-900 dark:border-dark-700">
    <!-- Header Section with Status -->
    <div class="p-4 sm:p-6 border-b border-gray-100 dark:border-dark-700">
      <div class="flex justify-between items-start gap-2">
        <div class="flex-1">
          <div class="flex items-center gap-3 mb-3">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-dark-100">
              Cash Register
            </h2>
          </div>
          <button id="button-stock"
            class="bg-blue-500 text-white px-3 py-2 text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center gap-1.5 min-h-[40px] md:px-6 md:py-3.5 md:text-lg md:min-h-[56px] lg:px-5 lg:py-2.5 lg:text-base lg:min-h-[46px]"
            :disabled="!isOpen" :class="{ 'opacity-50 cursor-not-allowed': !isOpen }"
            @click="showModalProductStock" type="button">
            <CubeIcon class="h-4 w-4 md:h-6 md:w-6 lg:h-5 lg:w-5" />
            Stock
          </button>
        </div>
        
        <!-- Right side status -->
        <div class="flex flex-col items-end gap-3">
          <div class="flex items-center gap-3">
            <div class="px-2.5 py-1 rounded-full text-xs font-medium" :class="isOpen
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
              ">
              {{ isOpen ? "OPEN" : "CLOSED" }}
            </div>
            <div class="flex items-center gap-1 text-sm text-gray-500 dark:text-dark-300">
              <ClockIcon class="h-4 w-4" />
              <span>{{ stopwatch }}</span>
            </div>
          </div>
          
          <!-- Close Register Button -->
          <button v-if="isOpen" id="button-close-register"
            class="bg-red-600 text-white px-3.5 py-2 text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-1.5 min-h-[40px] md:px-7 md:py-3.5 md:text-lg md:min-h-[56px] lg:px-6 lg:py-2.5 lg:text-base lg:min-h-[46px]"
            @click="showModal">
            <ArrowLeftOnRectangleIcon class="h-4 w-4 md:h-6 md:w-6 lg:h-5 lg:w-5" />
            Close Shift
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-4 sm:p-6">
      <div v-if="!isOpen" class="text-center py-12">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-dark-700">
          <ExclamationCircleIcon class="h-8 w-8 text-gray-400 dark:text-dark-300" />
        </div>
        <span class="text-gray-500 text-sm dark:text-dark-300">Cash register is not started</span>
      </div>
      
      <!-- Saldo Section -->
      <div v-if="isOpen && isDetail">
        <!-- Desktop View - Modern Card Layout -->
        <div class="hidden sm:block">
          <!-- Main Stats Row -->
          <div class="grid grid-cols-3 gap-4 mb-4">
            <div class="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 min-h-[140px] lg:min-h-[124px] flex flex-col justify-between dark:from-blue-950/40 dark:to-blue-900/40 dark:border-blue-800/60">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-blue-700 dark:text-blue-300">Initial Balance</span>
                <div class="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center dark:bg-blue-900/70">
                  <CurrencyDollarIcon class="h-4 w-4 text-blue-600 dark:text-blue-300" />
                </div>
              </div>
              <div class="text-2xl xl:text-[clamp(1.1rem,1.4vw,1.65rem)] leading-tight font-bold text-blue-900 tabular-nums dark:text-blue-100">
                {{ formatRupiah(initialBalance || 0) }}
              </div>
            </div>
            
            <div class="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4 min-h-[140px] lg:min-h-[124px] flex flex-col justify-between dark:from-green-950/40 dark:to-green-900/40 dark:border-green-800/60">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-green-700 dark:text-green-300">Total Transactions</span>
                <div class="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center dark:bg-green-900/70">
                  <ShoppingCartIcon class="h-4 w-4 text-green-600 dark:text-green-300" />
                </div>
              </div>
              <div class="text-2xl xl:text-[clamp(1.1rem,1.4vw,1.65rem)] leading-tight font-bold text-green-900 tabular-nums dark:text-green-100">
                {{ formatRupiah(totalTransaction) }}
              </div>
            </div>
            
            <div class="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-lg p-4 min-h-[140px] lg:min-h-[124px] flex flex-col justify-between dark:from-indigo-950/40 dark:to-indigo-900/40 dark:border-indigo-800/60">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-indigo-700 dark:text-indigo-300">Session Time</span>
                <div class="w-8 h-8 bg-indigo-200 rounded-full flex items-center justify-center dark:bg-indigo-900/70">
                  <ClockIcon class="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
                </div>
              </div>
              <div class="text-2xl xl:text-[clamp(1.1rem,1.4vw,1.65rem)] leading-tight font-bold text-indigo-900 tabular-nums dark:text-indigo-100">
                {{ stopwatch }}
              </div>
            </div>
          </div>
          
          <!-- Secondary Stats Row -->
          <div class="grid grid-cols-4 gap-4">
            <div class="bg-white border border-gray-200 rounded-lg p-3 min-h-[130px] lg:min-h-[116px] flex flex-col justify-between hover:shadow-md transition-shadow dark:bg-dark-800 dark:border-dark-600">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Closed Total</span>
              </div>
              <div class="text-xl xl:text-[clamp(0.9rem,1.05vw,1.3rem)] leading-tight font-semibold text-gray-900 tabular-nums dark:text-dark-100">
                {{ formatRupiah(totalClosedTransaction) }}
              </div>
              <div class="text-xs text-gray-500 dark:text-dark-400">{{ countClosedTransaction }} orders</div>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-3 min-h-[130px] lg:min-h-[116px] flex flex-col justify-between hover:shadow-md transition-shadow dark:bg-dark-800 dark:border-dark-600">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Pending Total</span>
              </div>
              <div class="text-xl xl:text-[clamp(0.9rem,1.05vw,1.3rem)] leading-tight font-semibold text-gray-900 tabular-nums dark:text-dark-100">
                {{ formatRupiah(totalPendingTransaction) }}
              </div>
              <div class="text-xs text-gray-500 dark:text-dark-400">{{ countPendingTransaction }} orders</div>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-3 min-h-[130px] lg:min-h-[116px] flex flex-col justify-between hover:shadow-md transition-shadow dark:bg-dark-800 dark:border-dark-600">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Closed Count</span>
              </div>
              <div class="text-4xl lg:text-3xl leading-none font-semibold text-gray-900 tabular-nums dark:text-dark-100">
                {{ countClosedTransaction }}
              </div>
              <div class="text-xs text-gray-500 dark:text-dark-400">transactions</div>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-3 min-h-[130px] lg:min-h-[116px] flex flex-col justify-between hover:shadow-md transition-shadow dark:bg-dark-800 dark:border-dark-600">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Pending Count</span>
              </div>
              <div class="text-4xl lg:text-3xl leading-none font-semibold text-gray-900 tabular-nums dark:text-dark-100">
                {{ countPendingTransaction }}
              </div>
              <div class="text-xs text-gray-500 dark:text-dark-400">transactions</div>
            </div>
          </div>
        </div>

        <!-- Mobile View with Accordion -->
        <div class="sm:hidden">
          <BaseAccordion>
            <template #header>
              <div class="flex items-center justify-between">
                <div>
                  <dt class="text-sm font-medium text-gray-600 dark:text-dark-300">
                    Transaction Summary
                  </dt>
                  <dd class="text-lg font-semibold text-gray-900 dark:text-dark-100">
                    {{ countClosedTransaction }} closed
                  </dd>
                </div>
              </div>
            </template>
            <template #content>
              <div class="grid grid-cols-2 gap-4">
                <div class="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-dark-800 dark:border-dark-600">
                  <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Initial</span>
                  <div class="text-sm font-semibold text-gray-900 dark:text-dark-100">{{
                    formatRupiah(
                      initialBalance == undefined || initialBalance == null
                        ? 0
                        : initialBalance
                    )
                  }}</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-dark-800 dark:border-dark-600">
                  <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Total</span>
                  <div class="text-sm font-semibold text-gray-900 dark:text-dark-100">
                    {{ formatRupiah(totalTransaction) }}
                  </div>
                </div>
                <div class="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-dark-800 dark:border-dark-600">
                  <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Closed</span>
                  <div class="text-sm font-semibold text-gray-900 dark:text-dark-100">{{
                    formatRupiah(totalClosedTransaction) }}</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-dark-800 dark:border-dark-600">
                  <span class="text-xs font-medium text-gray-600 dark:text-dark-300">Pending</span>
                  <div class="text-sm font-semibold text-gray-900 dark:text-dark-100">{{
                    formatRupiah(totalPendingTransaction) }}</div>
                </div>
              </div>
            </template>
          </BaseAccordion>
        </div>
      </div>

      <!-- Footer Section -->
      <div class="mt-8 flex flex-col sm:flex-row sm:justify-between gap-4">
        <button id="button-new-transaction"
          class="flex-1 sm:flex-none bg-green-600 text-white px-6 py-2.5 text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center justify-center gap-2 min-h-[44px] md:px-10 md:py-4 md:text-lg md:min-h-[60px] lg:px-8 lg:py-3 lg:text-base lg:min-h-[48px]"
          :disabled="!isOpen" :class="{ 'opacity-50 cursor-not-allowed': !isOpen }" @click="createOrder">
          <ShoppingCartIcon class="h-4 w-4 md:h-6 md:w-6 lg:h-5 lg:w-5" />
          New Transaction
        </button>

        <!-- Open Register Button (only when closed) -->
        <button v-if="!isOpen" id="button-open-register"
          class="flex-1 sm:flex-none bg-blue-600 text-white px-6 py-3 text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2"
          @click="showModal">
          <ArrowRightOnRectangleIcon class="h-4 w-4" />
          Open Register
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatRupiah } from "@/helpers/formatRupiah";
import ModalCashRegister from "./modals/ModalCashRegister.vue";
import { mapActions, mapGetters } from "vuex";
import {
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ShoppingCartIcon,
  ClockIcon,
  CubeIcon,
  CurrencyDollarIcon,
} from "@heroicons/vue/24/solid";
import { ExclamationCircleIcon } from "@heroicons/vue/24/outline";
import BaseAccordion from "@/components/base/BaseAccordion.vue";
import ModalImportMarketplace from "@/components/modals/ModalImportMarketplace.vue";
import ModalImportTiktok from "@/components/modals/ModalImportTiktok.vue";
import ModalChooseCustomer from "@/components/modals/ModalChooseCustomer.vue";
import { ConfirmDialog, Button } from "primevue";
import ModalProductStock from "@/components/modals/ModalProductStock.vue";

export default {
  name: "CashRegister",
  components: {
    ModalCashRegister, 
    ArrowRightOnRectangleIcon, 
    ArrowLeftOnRectangleIcon, 
    ShoppingCartIcon, 
    ClockIcon,
    CubeIcon,
    CurrencyDollarIcon,
    ExclamationCircleIcon, 
    BaseAccordion, 
    ModalImportMarketplace,
    ModalImportTiktok, 
    ModalChooseCustomer, 
    ConfirmDialog, 
    Button, 
    ModalProductStock
  },
  data() {
    return {
      modalShowCashRegister: false,
      modalShowProductStock: false,
      isBlur: false,
      stopwatch: "00:00:00",
      elapsed: "00:00:00",
      startTime: null,
      difference: 0,
      allCashRegisters: [],
      modalShowImportMarketplace: false,
      modalShowImportTiktok: false,
      product: null,
      dropdownOpen: false,
      modalShowCustomer: false,
    };
  },
  computed: {
    ...mapGetters("sales_orders", ["allSalesOrders"]),
    ...mapGetters("petty_cash", ["allPettyCashes"]),
    relevantCashRegister() {
      return this.allCashRegisters.find((cr) => cr.status == "open");
    },
    isDetail() {
      const isAllowed = ["1", "2"];
      return localStorage.getItem("role_id") == "null" || isAllowed.includes(localStorage.getItem("role_id"));
    },
    totalDepositPettyCash() {
      return this.allPettyCashes.reduce((sum, pc) => sum + (this.relevantCashRegister.id == pc.cash_register_id && pc.amount_deposit ? parseFloat(pc.amount_deposit) : 0), 0);
    },
    totalWithdrawalPettyCash() {
      return this.allPettyCashes.reduce((sum, pc) => sum + (this.relevantCashRegister.id == pc.cash_register_id && pc.amount_withdrawal ? parseFloat(pc.amount_withdrawal) : 0), 0);
    },
    initialBalance() {
      return this.relevantCashRegister?.start_saldo;
    },
    endBalance() {
      return this.relevantCashRegister?.end_saldo;
    },
    isOpen() {
      return (
        this.relevantCashRegister != null &&
        this.relevantCashRegister != undefined
      );
    },
    totalTransaction() {
      const salesOrders = [...this.allSalesOrders];
      return salesOrders.reduce((sum, item) => {
        const grandTotal = parseFloat(item.grand_total);
        let addition =
          grandTotal == undefined || grandTotal == null ? 0 : grandTotal;
        return sum + addition;
      }, 0);
    },
    totalClosedTransaction() {
      return this.allSalesOrders
        .filter((so) => so.status == "paid")
        .reduce((sum, item) => {
          const grandTotal = parseFloat(item.grand_total);
          let addition =
            grandTotal == undefined || grandTotal == null ? 0 : grandTotal;
          return sum + addition;
        }, 0);
    },
    countClosedTransaction() {
      return this.allSalesOrders.filter((so) => so.status == "paid").length;
    },
    totalPendingTransaction() {
      return this.allSalesOrders
        .filter((so) => so.status == "pending")
        .reduce((sum, item) => {
          const grandTotal = parseFloat(item.grand_total);
          let addition =
            grandTotal == undefined || grandTotal == null ? 0 : grandTotal;
          return sum + addition;
        }, 0);
    },
    countPendingTransaction() {
      return this.allSalesOrders.filter((so) => so.status == "pending").length;
    },
  },
  created() {
    this.refreshCashRegister();
  },
  methods: {
    ...mapActions("cash_registers", [
      "createCashRegister",
      "closeCashRegister",
    ]),
    ...mapActions("petty_cash", ["fetchPettyCashes"]),
    ...mapActions("sales_orders", ["createSalesOrder"]),
    formatRupiah,
    handleImportMarketplace() {
      this.dropdownOpen = false;
      this.showModalImportMarketplace();
    },
    handleImportTiktok() {
      this.dropdownOpen = false;
      this.showModalImportTiktok();
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
      }).format(value);
    },
    startStopwatch() {
      let seconds = this.isOpen
        ? Math.floor(
          (new Date() - new Date(this.relevantCashRegister.time_start)) / 1000
        )
        : 0;
      this.timeInterval = setInterval(() => {
        seconds++;
        const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
        const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
        const secs = String(seconds % 60).padStart(2, "0");
        this.stopwatch = `${hrs}:${mins}:${secs}`;

        // Update Elapsed Time
        if (this.isOpen) {
          this.elapsed = `${hrs}:${mins}:${secs}`;
        }
      }, 1000);
    },
    stopStopwatch() {
      clearInterval(this.timeInterval);
      this.stopwatch = "00:00:00";
    },
    showModal() {
      this.modalShowCashRegister = true;
      this.isBlur = true;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowCashRegister = false;
        this.isBlur = false;
      }, 200);
    },
    showModalProductStock() {
      this.modalShowProductStock = true;
      this.isBlur = true;
    },
    closeModalProductStock() {
      setTimeout(() => {
        this.modalShowProductStock = false;
        this.isBlur = false;
      }, 200);
    },
    async refreshCashRegister() {
      this.allCashRegisters = [];
      try {
        this.stopStopwatch();
        await this.fetchPettyCashes();

        const response = await this.$store.dispatch(
          "cash_registers/fetchOpenCashRegisters"
        );
        if (response) {
          this.allCashRegisters = response.data;
        }
        if (this.isOpen) {
          this.$store.dispatch("sales_orders/fetchSalesOrders", { limit: 1000, cash_register_id: this.relevantCashRegister.id });
          this.startStopwatch();
        }
      } catch (error) {
        console.log(error);
        this.allCashRegisters = [];

        this.$toast.add({
          severity: "error",
          summary: "Failed",
          detail: error.response.data.message,
          life: 5000,
        });
      }
    },
    createNewTransaction() {
      this.$router.push({ name: "order" });
    },
    closeCustomerModal() {
      this.modalShowCustomer = false;
      console.log(this.transaction);
    },
    showModalCustomer() {
      this.modalShowCustomer = true;
    },
    async createOrder() {
      try {
        const response = await this.createSalesOrder({
          table_id: null,
          marketing_id: null,
          cash_register_id: this.relevantCashRegister.id,
          transaction_type_id: 1,
          customer_name: "",
          customer_phone: "",
          customer_id: null,
          sub_total: 0,
          grand_total: 0,
          status: "pending",
          order_type: 'take_away',
          payment_amount: 0,
          payment_change: 0,
          tax: 0,
          service_charge: 0,
          discount: 0,
          note: "",
        });

        const orderId = response.data.id;

        this.$router.push({
          name: "order",
          query: {
            orderId
          },
        });
      } catch (error) {
        console.error("Error processing order:", error);
        alert("Terjadi kesalahan saat memproses pesanan");

        if (this.selectedTableId) {
          try {
            await this.updateTable({
              id: this.selectedTableId,
              status: "available",
            });
          } catch (revertError) {
            console.error("Error reverting table status:", revertError);
          }
        }
      }
    },
    showModalImportMarketplace() {
      this.modalShowImportMarketplace = true;
    },
    closeModalImportMarketplace() {
      this.modalShowImportMarketplace = false;
    },
    showModalImportTiktok() {
      this.modalShowImportTiktok = true;
    },
    closeModalImportTiktok() {
      this.modalShowImportTiktok = false;
    },
    emit: ["show-dialog"],
  },
};
</script>
